import { Injectable } from '@angular/core';

/**
 * Captura e persiste atributos de marketing (GCLID, GBRAID, WBRAID) vindos da URL
 * para uso posterior em Offline Conversion Import do Google Ads.
 *
 * Estratégia: a primeira visita com gclid na URL grava o valor em localStorage com
 * timestamp; visitas subsequentes sem gclid mantêm o valor (atribuição "first touch").
 * O TTL é de 90 dias — alinhado ao lookback window padrão de conversões do Google Ads.
 */
@Injectable({ providedIn: 'root' })
export class MarketingAttributionService {
    private readonly STORAGE_KEY = 'mkt_attribution_v1';
    private readonly TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 dias

    /**
     * Inspeciona a URL atual e, se houver gclid/gbraid/wbraid, persiste em localStorage.
     * Idempotente — pode ser chamado em todo NavigationEnd. Não sobrescreve um GCLID
     * existente válido por outro gerado em outra visita (first touch).
     */
    captureFromUrl(): void {
        try {
            const params = new URLSearchParams(window.location.search);
            const gclid = params.get('gclid')?.trim();
            const gbraid = params.get('gbraid')?.trim();
            const wbraid = params.get('wbraid')?.trim();

            if (!gclid && !gbraid && !wbraid) {
                return;
            }

            const existing = this.read();
            // Se já temos um GCLID válido, mantemos (first touch).
            if (existing && this.isValid(existing) && existing.gclid) {
                return;
            }

            const payload: AttributionPayload = {
                gclid: gclid || existing?.gclid || null,
                gbraid: gbraid || existing?.gbraid || null,
                wbraid: wbraid || existing?.wbraid || null,
                capturedAt: Date.now()
            };

            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(payload));
        } catch {
            // Falhas de localStorage (modo privado, quota) são silenciosas.
        }
    }

    /**
     * Retorna o GCLID válido (não expirado) ou null.
     * Prioriza GCLID; se ausente, retorna o primeiro entre GBRAID/WBRAID disponível.
     */
    getGclid(): string | null {
        const data = this.read();
        if (!data || !this.isValid(data)) {
            return null;
        }
        return data.gclid || data.gbraid || data.wbraid || null;
    }

    /** Limpa atribuição (útil para testes). */
    clear(): void {
        try { localStorage.removeItem(this.STORAGE_KEY); } catch { /* ignore */ }
    }

    private read(): AttributionPayload | null {
        try {
            const raw = localStorage.getItem(this.STORAGE_KEY);
            if (!raw) return null;
            return JSON.parse(raw) as AttributionPayload;
        } catch {
            return null;
        }
    }

    private isValid(p: AttributionPayload): boolean {
        return typeof p.capturedAt === 'number' && (Date.now() - p.capturedAt) < this.TTL_MS;
    }
}

interface AttributionPayload {
    gclid: string | null;
    gbraid: string | null;
    wbraid: string | null;
    capturedAt: number;
}
